import { useEffect, useState } from 'react'
import ApplicationStep1 from './ApplicationSteps/ApplicationStep1'
import { apiURL } from '../../utilities/URLs'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import TagManager from 'react-gtm-module'
import { ApplicationClass } from './classes/ApplicationClass'
import { format } from 'date-fns'

TagManager.initialize({
  gtmId: process.env.REACT_APP_GTM_ID
})

export default function Application() {
  const navigate = useNavigate()

  const { affiliateName } = useParams()
  const [searchParams] = useSearchParams()
  const slug = searchParams.get('slug') ?? ''
  const agentId = searchParams.get('agentId') ?? ''

  const [application, setApplication] = useState(new ApplicationClass())
  const [emailOrPhoneInUse, setEmailOrPhoneInUse] = useState(false)
  const [step, setStep] = useState(0)
  const [registrationLink, setRegistrationLink] = useState('')

  /**
   * @param {String} firstName 
   * @param {String} lastName 
   * @param {String} email 
   * @param {String} phone
   * @param {String} entityName
   * @param {String} entityState
   * @param {String} remainingERCCredits
   * @param {String} withPEO
   * @param {String} filedAfterDate
   * @param {String[]} quarters
   * @param {String} receivedNotification 
   */
  const handleApplicationStep1 = (firstName, lastName, email, phone, entityName, entityState, remainingERCCredits, withPEO, filedAfterDate, quarters) => {
    setApplication((prevApplication) => ({
      ...prevApplication,
      firstName,
      lastName,
      email,
      phone: '+' + phone,
      entityName,
      entityState,
      remainingERCCredits,
      withPEO,
      filedAfterDate,
      quarters,
      finishedApplication: true
    }))
  }

  const handleSubmitApplication = async () => {
    application.affiliateName = affiliateName
    application.slug = slug
    application.agentId = agentId

    const applicationResponse = await fetch(`${apiURL}/api/applications/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(application)
    })
    const applicationData = await applicationResponse.json()

    if (applicationData.response.error) {
      if (applicationData.response.error === 'Email or Phone In Use.')
        setEmailOrPhoneInUse(true)
      console.error(applicationData.response.error)
    } else {
      TagManager.dataLayer({
        dataLayer: {
          event: 'conversion',
          sent_to: 'AW-16965836493/PEQtCL6HubIaEM29-Jk',
          value: 1.0,
          currency: 'USD'
        }
      })

      if (application.withPEO === 'Yes')
        setStep(1)
      else if (application.filedAfterDate === 'Yes' && !application.quarters.includes('q12021') && !application.quarters.includes('q22021'))
        setStep(2)
      else {
        if (applicationData.response.registrationLink)
          setRegistrationLink(applicationData.response.registrationLink)
        else
          window.top.location.href = `${apiURL}/callback?email=${application.email}&code=${applicationData.response.code}&redirect=${applicationData.response.redirect}`
      }
    }
  }

  useEffect(() => {
    if (application.finishedApplication)
      handleSubmitApplication()
  }, [application.finishedApplication])

  return (
    <div className='w-screen min-h-screen flex flex-col items-center bg-white text-gray-900 overflow-auto'>
      {!emailOrPhoneInUse ? (
        !registrationLink ? (
          step === 0 ? (
            <ApplicationStep1
              handleApplicationStep1={handleApplicationStep1}
              finishedApplication={application.finishedApplication}
            />
          ) : step === 1 ? (
            <div className='w-11/12 max-w-2xl flex flex-col items-center text-center bg-white p-8 rounded-3xl shadow-sm border border-gray-100 gap-6 mt-12'>
              <p className='text-slate-900 leading-relaxed font-bold text-xl'>We're sorry - we cannot assist with your ERC claim at this time.</p>
              <p className='text-slate-600'>Claims involving Professional Employer Organizations (PEOs) require direct handling through the PEO, as they are the filer of record with the IRS. Accelerate specializes in direct employer claims only. We recommend contacting your PEO provider for updates or escalation options. Thank you for your interest.</p>
              <button className='px-6 py-3 rounded-xl shadow-md bg-ertc-primary text-white mt-4 font-bold active:scale-95 transition-transform' onClick={() => window.top.location.href = 'https://ertcrefund.com'}>
                Return Home
              </button>
            </div>
          ) : (
            <div className='w-11/12 max-w-2xl flex flex-col items-center text-center bg-white p-8 rounded-3xl shadow-sm border border-gray-100 gap-6 mt-12'>
              <p className='text-slate-900 leading-relaxed font-bold text-xl'>We're sorry - your claim is not eligible for advocacy.</p>
              <p className='text-slate-600'>Under the One Big Beautiful Bill Act (enacted July 2025), the IRS cannot allow or pay ERC refunds for Q3/Q4 2021 on claims filed after January 31, 2024, even if otherwise eligible. Since your claim covers only later 2021 quarters and was filed after this cutoff, no payment is possible, and we unfortunately cannot assist. Thank you for your interest.</p>
              <button className='px-6 py-3 rounded-xl shadow-md bg-ertc-primary text-white mt-4 font-bold active:scale-95 transition-transform' onClick={() => window.top.location.href = 'https://ertcrefund.com'}>
                Return Home
              </button>
            </div>
          )
        ) : (
          <div className='flex flex-col h-full items-center justify-center gap-6 max-w-2xl mx-auto p-8'>
            <div className='text-center'>
              <h2 className='text-3xl font-bold text-emerald-600 mb-4'>Success!</h2>
              <p className='text-lg text-slate-700'>You have successfully chosen Accelerate. Click the button below to register for the Accelerate Portal.</p>
            </div>
            <button
              onClick={() => window.top.location.href = registrationLink}
              className='bg-ertc-primary text-white font-bold px-8 py-3 rounded-xl shadow-md active:scale-95 transition-transform'
            >
              Register for Accelerate Portal
            </button>
          </div>
        )
      ) : (
        <div className='text-center space-y-6 mt-16 max-w-md mx-auto p-8 rounded-3xl bg-white border border-gray-100 shadow-sm'>
          <p className='text-slate-700 text-lg'>It seems the information you entered is already in our system. Please click the button below to login.</p>
          <button
            onClick={() => window.top.location.href = 'https://myportal.ertcrefund.com/'}
            className='w-full bg-ertc-primary text-white font-bold px-6 py-3 rounded-xl shadow-md active:scale-95 transition-transform'
          >
            Login
          </button>
        </div>
      )}
    </div>
  )
}